import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  TextInput,
  StatusBar,
  Alert,
  Modal,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { COLORS, SPACING, RADIUS, FONT_SIZE } from '../config/theme';
import { errorHandler, ErrorCategory, ErrorSeverity } from '../utils/errorHandler';
import { Card } from '../components/Card';
import { type Product } from '../lib/supabase';
import { authService } from '../services/authService';
import { orderService } from '../services/orderService';
import { userService } from '../services/userService';
import { useAuthStore, useCartStore } from '../store';
import * as Clipboard from 'expo-clipboard';

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
  description: string;
  providers: string[];
}

export const PaymentScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const {
    amount = 0,
    orderId: routeOrderId,
    storeId,
    items: routeItems,
    customer: routeCustomer,
    itemsJson,
    customerJson,
    paymentMethod,
  } = route.params || {};

  const orderId = routeOrderId || `ORD-${Date.now()}`;

  const { user, showAuthModal } = useAuthStore();
  const { clearCart, removeItem } = useCartStore();

  const parseJsonParam = <T,>(value: any, fallback: T): T => {
    if (!value || typeof value !== 'string') return fallback;
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  };

  const items = Array.isArray(routeItems)
    ? routeItems
    : parseJsonParam<Array<{ product: Product; quantity: number }>>(itemsJson, []);
  const customer = routeCustomer || parseJsonParam<any>(customerJson, undefined);

  // Force default payment method to cash and disable other options for now
  const [selectedMethod, setSelectedMethod] = useState<string>(paymentMethod || 'cash');
  const [phoneNumber, setPhoneNumber] = useState(customer?.phone || '');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCVV, setCardCVV] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // completed: prevents the user from re-submitting after an order was created
  const [completed, setCompleted] = useState(false);
  const [orderSuccessModalVisible, setOrderSuccessModalVisible] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'mobile_money',
      name: 'Mobile Money',
      icon: 'phone-portrait-outline',
      description: 'Paiement via Airtel Money et Moov',
      providers: ['Airtel Money', 'Moov Money'],
    },
    {
      id: 'card',
      name: 'Carte Bancaire',
      icon: 'card-outline',
      description: 'Visa, Mastercard...',
      providers: ['Visa', 'Mastercard', 'American Express'],
    },
    {
      id: 'bank_transfer',
      name: 'Virement Bancaire',
      icon: 'swap-horizontal-outline',
      description: 'Virement direct',
      providers: ['Compte Benin', 'Compte Nigeria', 'Compte International'],
    },
    {
      id: 'cash',
      name: 'À la Livraison',
      icon: 'cash-outline',
      description: 'Paiement à la réception',
      providers: [],
    },
  ];

  const handlePayment = () => {
    if (selectedMethod === 'mobile_money' && !phoneNumber.trim()) {
      Alert.alert('Erreur', 'Veuillez entrer votre numéro tél.');
      return;
    }
    if (selectedMethod === 'card' && (!cardNumber.trim() || !cardExpiry.trim() || !cardCVV.trim())) {
      Alert.alert('Erreur', 'Veuillez compléter les données de la carte');
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmPayment = async () => {
    setShowConfirmModal(false);
    
    // Bloquer les paiements en ligne pour le prototype
    if (selectedMethod === 'mobile_money' || selectedMethod === 'card') {
      Alert.alert(
        'Bientôt disponible', 
        "L'intégration du paiement n'est pas encore finalisée. Veuillez choisir le paiement à la livraison pour le moment."
      );
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      if (!storeId) throw new Error('storeId manquant');
      let userId = user?.id;
      if (!userId) {
        try {
          const userObj = await authService.getCurrentUser();
          userId = userObj?.id || undefined;
        } catch {
          // ignore, will try anonymous sign-in below
        }


      }

      if (!userId) {
        setProcessing(false);
        setErrorMessage('Veuillez vous connecter pour passer commande');
        Alert.alert('Connexion requise', 'Veuillez vous connecter pour passer commande', [
          {
            text: 'Se connecter',
            onPress: () => showAuthModal({ type: 'CHECKOUT', payload: { params: route.params } }),
          },
          { text: 'Annuler', style: 'cancel' },
        ]);
        return;
      }
      if (!Array.isArray(items) || items.length === 0) throw new Error('Panier vide');

      await userService.upsertProfile(userId, {
        full_name: customer?.name || 'Client',
        phone: phoneNumber,
        whatsapp_number: phoneNumber,
      });

      const isExisting = !!route.params?.existingOrder;

      if (isExisting) {
        // Un paiement depuis PaymentScreen sur une commande existante n'est plus autorisé côté client.
        // Le statut de paiement doit être géré par le backend (webhook FedaPay) ou par le vendeur.
        const existingOrderId = routeOrderId;
        if (!existingOrderId) throw new Error('orderId manquant pour paiement existant');

        try {
          // On se contente d'afficher le succès et d'ajouter une notification client-side (best-effort)
          try {
            const { storeService } = await import('../services/storeService');
            const { notificationService } = await import('../services/notificationService');
            if (storeId) {
              const store = await storeService.getById(String(storeId));
              if (store?.user_id) {
                await notificationService.create({
                  user_id: store.user_id,
                  title: 'Nouvelle commande 🛍️',
                  body: `Vous avez reçu une nouvelle commande de ${customer?.name || 'un client'} pour un montant de ${(amount / 1000).toFixed(0)} KCFA.`,
                  type: 'order',
                  data: { orderId: existingOrderId, storeId },
                });
              }
            }
          } catch (e: any) {
            console.warn('notification creation failed for existing order', e);
          }

          // remove paid items from cart
          try {
            if (Array.isArray(items) && items.length > 0) {
              for (const it of items) {
                try { removeItem(it.product.id); } catch (e: any) { /* ignore */ }
              }
            }
          } catch (e: any) {
            console.warn('failed to remove items from cart after existing order payment', e);
          }

          // mark completed to prevent further submissions and keep UI disabled
          setCompleted(true);
          setCreatedOrderId(existingOrderId);
          setOrderSuccessModalVisible(true);
        } catch (e: any) {
          throw e;
        }
      } else {
        // existing flow: create order then process
        let created: any;
        const paymentMethodString = selectedMethod === 'cash'
          ? 'cash_on_delivery'
          : selectedMethod === 'card'
            ? 'card'
            : 'mobile_money';

        created = await orderService.createOnlineOrder({
          store_id: String(storeId),
          customer_name: customer?.name || user?.full_name || 'Client',
          customer_phone: phoneNumber,
          shipping_address: customer?.address,
          payment_method: paymentMethodString,
          notes: customer?.notes,
          items: items.map((it: { product: Product; quantity: number }) => ({
            product_id: it.product.id,
            quantity: it.quantity,
          })),
        });

        // Decrement stock + notify seller (best effort)
        // Le frontend n'appelle plus processPayment. La commande reste pending.
        // Le webhook serveur ou le vendeur s'occupera de la suite.

        // Notification créée par le RPC process_order_after_payment, mais on en ajoute une côté client 
        // pour garantir une réception instantanée et fiable même si le RPC a un délai ou échoue.
        try {
          const { storeService } = await import('../services/storeService');
          const { notificationService } = await import('../services/notificationService');
          
          if (storeId) {
            const store = await storeService.getById(String(storeId));
            if (store?.user_id) {
              await notificationService.create({
                user_id: store.user_id,
                title: 'Nouvelle commande 🛍️',
                body: `Vous avez reçu une nouvelle commande de ${customer?.name || 'un client'} pour un montant de ${(amount / 1000).toFixed(0)} KCFA.`,
                type: 'order',
                data: {
                  orderId: created?.order_id,
                  storeId: storeId,
                }
              });
            }
          }
        } catch (e: any) {
          console.warn('client side notification creation failed', e);
        }

        setCreatedOrderId(created.order_id);
        setOrderSuccessModalVisible(true);
        
        clearCart();

        // block further submissions immediately
        setCompleted(true);
        setCreatedOrderId(created?.id || orderId);
        setOrderSuccessModalVisible(true);
      }
    } catch (e: any) {
      errorHandler.handle(e, 'payment confirm failed', ErrorCategory.SYSTEM, ErrorSeverity.LOW);
      const msg =
        typeof e?.message === 'string'
          ? e.message
          : typeof e === 'string'
            ? e
            : 'Impossible de finaliser la commande';
      setErrorMessage(msg);

      if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof (window as any).alert === 'function') {
        (window as any).alert(`Erreur\n\n${msg}`);
      }

      Alert.alert('Erreur', msg);
    } finally {
      // keep button disabled if completed, otherwise reset processing
      if (!completed) setProcessing(false);
    }
  };

  const formatCurrency = (value: number) => {
    return (value / 1000).toFixed(0) + ' KCFA';
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.bg} />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={COLORS.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Paiement</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView showsVerticalScrollIndicator={false} style={styles.content}>
        {/* Order Summary */}
        <View style={styles.summaryCard}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Numéro de commande</Text>
            <Text style={styles.summaryValue}>{orderId}</Text>
          </View>
          <View style={[styles.summaryRow, styles.divider]}>
            <Text style={styles.summaryLabel}>Montant à payer</Text>
            <Text style={styles.amountValue}>{formatCurrency(amount)}</Text>
          </View>
        </View>

        {Array.isArray(items) && items.length > 0 && (
          <View style={styles.summaryCard}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Articles</Text>
              <Text style={styles.summaryValue}>{items.length}</Text>
            </View>
            {items.slice(0, 5).map((it: { product: Product; quantity: number }) => (
              <View key={it.product.id} style={styles.summaryRow}>
                <Text style={styles.summaryLabel} numberOfLines={1}>
                  {it.product.name} × {it.quantity}
                </Text>
                <Text style={styles.summaryValue}>
                  {(it.product.price * it.quantity).toLocaleString()} FCA
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Payment Methods */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Méthode de paiement</Text>
          {paymentMethods.filter(m => m.id === 'cash').map((method) => (
            <TouchableOpacity
              key={method.id}
              style={[
                styles.methodCard,
                selectedMethod === method.id && styles.methodCardSelected,
              ]}
              // disabled: only cash is selectable
              onPress={() => { if (method.id === 'cash') setSelectedMethod(method.id); }}
            >
              <View style={styles.methodIcon}>
                <Ionicons
                  name={method.icon as any}
                  size={24}
                  color={selectedMethod === method.id ? COLORS.accent : COLORS.textMuted}
                />
              </View>
              <View style={styles.methodInfo}>
                <Text style={styles.methodName}>{method.name}</Text>
                <Text style={styles.methodDesc}>{method.description}</Text>
              </View>
              <Ionicons
                name={selectedMethod === method.id ? 'checkmark-circle' : 'ellipse-outline'}
                size={24}
                color={selectedMethod === method.id ? COLORS.accent : COLORS.border}
              />
            </TouchableOpacity>
          ))}
        </View>

        {/* Payment Details Form */}
        {selectedMethod === 'mobile_money' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Détails Mobile Money</Text>
            <TextInput
              style={styles.input}
              placeholder="Numéro tél. (ex: +241 90123456)"
              placeholderTextColor={COLORS.textMuted}
              keyboardType="phone-pad"
              value={phoneNumber}
              onChangeText={setPhoneNumber}
            />
            <Text style={styles.helperText}>
              Vous recevrez une confirmation sur ce numéro
            </Text>
          </View>
        )}

        {selectedMethod === 'card' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Données de la carte</Text>
            <TextInput
              style={styles.input}
              placeholder="Numéro de carte (16 chiffres)"
              placeholderTextColor={COLORS.textMuted}
              keyboardType="numeric"
              maxLength={16}
              value={cardNumber}
              onChangeText={setCardNumber}
            />
            <View style={styles.cardRow}>
              <TextInput
                style={[styles.input, styles.cardInput]}
                placeholder="MM/YY"
                placeholderTextColor={COLORS.textMuted}
                value={cardExpiry}
                onChangeText={setCardExpiry}
              />
              <TextInput
                style={[styles.input, styles.cardInput]}
                placeholder="CVV"
                placeholderTextColor={COLORS.textMuted}
                keyboardType="numeric"
                maxLength={4}
                secureTextEntry
                value={cardCVV}
                onChangeText={setCardCVV}
              />
            </View>
          </View>
        )}

        {selectedMethod === 'bank_transfer' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Informations bancaires</Text>
            <Card style={styles.bankCard}>
              <View style={styles.bankInfo}>
                <Text style={styles.bankLabel}>Titulaire du compte</Text>
                <Text style={styles.bankValue}>LibreShop SARL</Text>
              </View>
              <View style={styles.bankInfo}>
                <Text style={styles.bankLabel}>IBAN</Text>
                <Text style={styles.bankValue}>BJ26 1556 2000 0100 0001 0001 00</Text>
              </View>
              <View style={styles.bankInfo}>
                <Text style={styles.bankLabel}>BIC</Text>
                <Text style={styles.bankValue}>ECOPBJJ</Text>
              </View>
              <View style={styles.bankInfo}>
                <Text style={styles.bankLabel}>Montant</Text>
                <Text style={styles.bankValue}>{formatCurrency(amount)}</Text>
              </View>
              <Text style={styles.bankNote}>
                Veuillez copier ces informations et effectuer le virement depuis votre banque.
              </Text>
            </Card>
          </View>
        )}

        {selectedMethod === 'cash' && (
          <View style={styles.section}>
            <Card style={styles.cashCard}>
              <Ionicons name="checkmark-circle" size={48} color={COLORS.success} />
              <Text style={styles.cashTitle}>Paiement à la Livraison</Text>
              <Text style={styles.cashDesc}>
                Vous paierez le montant de {formatCurrency(amount)} lors de la réception de votre commande.
              </Text>
            </Card>
          </View>
        )}
      </ScrollView>

      {/* Payment Button */}
      <View style={styles.footer}>
        {!!errorMessage && (
          <Text style={styles.errorText} numberOfLines={3}>
            {errorMessage}
          </Text>
        )}
        <Pressable
          style={[styles.payButton, (processing || completed) && styles.payButtonDisabled]}
          onPress={handlePayment}
          disabled={processing || completed}
          hitSlop={10}
        >
          {processing ? (
            <ActivityIndicator color={COLORS.text} />
          ) : (
            <>
              <Ionicons name="lock-closed-outline" size={20} color={COLORS.text} />
              <Text style={styles.payButtonText}>
                Commander {formatCurrency(amount)}
              </Text>
            </>
          )}
        </Pressable>
      </View>

      {/* Confirmation Modal */}
      <Modal
        visible={showConfirmModal}
        animationType="fade"
        transparent
        onRequestClose={() => setShowConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmModal}>
            <Text style={styles.confirmTitle}>Confirmer la commande</Text>
            <View style={styles.confirmInfo}>
              <Text style={styles.confirmLabel}>Méthode</Text>
              <Text style={styles.confirmValue}>
                {paymentMethods.find(m => m.id === selectedMethod)?.name}
              </Text>
            </View>
            <View style={styles.confirmInfo}>
              <Text style={styles.confirmLabel}>Montant</Text>
              <Text style={styles.confirmValueLarge}>{formatCurrency(amount)}</Text>
            </View>
            <View style={styles.confirmActions}>
              <Pressable
                style={styles.confirmButton}
                onPress={() => setShowConfirmModal(false)}
                hitSlop={10}
              >
                <Text style={styles.confirmButtonText}>Annuler</Text>
              </Pressable>
              <Pressable
                style={[styles.confirmButton, styles.confirmButtonPrimary]}
                onPress={confirmPayment}
                hitSlop={10}
              >
                <Text style={[styles.confirmButtonText, styles.confirmButtonTextPrimary]}>
                  Confirmer
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Order Success Modal */}
      <Modal visible={orderSuccessModalVisible} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={[styles.confirmModal, { alignItems: 'center', padding: SPACING.xl }]}>
            <View style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: COLORS.success + '20', alignItems: 'center', justifyContent: 'center', marginBottom: SPACING.md }}>
              <Ionicons name="checkmark-circle" size={40} color={COLORS.success} />
            </View>
            <Text style={[styles.confirmTitle, { marginBottom: SPACING.xs }]}>Commande envoyée avec succès !</Text>
            <Text style={{ fontSize: FONT_SIZE.sm, color: COLORS.textMuted, textAlign: 'center', marginBottom: SPACING.lg }}>
              Votre commande a bien été enregistrée.
            </Text>

            <View style={{ backgroundColor: COLORS.bg, padding: SPACING.md, borderRadius: RADIUS.md, width: '100%', marginBottom: SPACING.lg }}>
              <Text style={{ fontSize: FONT_SIZE.xs, color: COLORS.textMuted, marginBottom: 4 }}>Numéro de commande</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: FONT_SIZE.md, fontWeight: '700', color: COLORS.text, letterSpacing: 1 }}>
                  {createdOrderId ? createdOrderId.split('-')[0] : ''}
                </Text>
                <TouchableOpacity
                  onPress={async () => {
                    if (createdOrderId) {
                      await Clipboard.setStringAsync(createdOrderId.split('-')[0]);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }
                  }}
                  style={{ padding: 6, backgroundColor: COLORS.card, borderRadius: RADIUS.sm, flexDirection: 'row', alignItems: 'center', gap: 4 }}
                >
                  <Ionicons name={copied ? "checkmark" : "copy-outline"} size={16} color={copied ? COLORS.success : COLORS.accent} />
                  <Text style={{ fontSize: FONT_SIZE.xs, color: copied ? COLORS.success : COLORS.accent, fontWeight: '600' }}>
                    {copied ? 'Copié' : 'Copier'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={{ backgroundColor: COLORS.accent + '10', padding: SPACING.md, borderRadius: RADIUS.md, width: '100%', marginBottom: SPACING.xl, flexDirection: 'row', gap: SPACING.sm }}>
              <Ionicons name="information-circle-outline" size={20} color={COLORS.accent} style={{ marginTop: 2 }} />
              <Text style={{ flex: 1, fontSize: FONT_SIZE.xs, color: COLORS.textSoft, lineHeight: 18 }}>
                Pour suivre les étapes de votre commande, rendez-vous dans l'onglet <Text style={{ fontWeight: '700', color: COLORS.accent }}>Commandes</Text> sur la barre de navigation et utilisez ce code.
              </Text>
            </View>

            <TouchableOpacity
              style={[styles.payButton, { width: '100%' }]}
              onPress={() => {
                setOrderSuccessModalVisible(false);
                navigation.navigate('ClientTabs');
              }}
            >
              <Text style={styles.payButtonText}>Continuer</Text>
              <Ionicons name="arrow-forward" size={18} color={COLORS.text} />
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.bg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.lg,
    paddingTop: SPACING.xxl,
  },
  backButton: {
    padding: SPACING.sm,
  },
  headerTitle: {
    fontSize: FONT_SIZE.xl,
    fontWeight: '700',
    color: COLORS.text,
  },
  headerRight: {
    width: 44,
  },
  content: {
    flex: 1,
    paddingHorizontal: SPACING.lg,
  },
  summaryCard: {
    backgroundColor: COLORS.accent + '20',
    borderRadius: RADIUS.lg,
    padding: SPACING.lg,
    marginBottom: SPACING.xl,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.accent,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  divider: {
    marginTop: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
  },
  summaryLabel: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
  },
  summaryValue: {
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    color: COLORS.text,
  },
  amountValue: {
    fontSize: FONT_SIZE.xl,
    fontWeight: '700',
    color: COLORS.accent,
  },
  section: {
    marginBottom: SPACING.xl,
  },
  sectionTitle: {
    fontSize: FONT_SIZE.lg,
    fontWeight: '700',
    color: COLORS.text,
    marginBottom: SPACING.md,
  },
  methodCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.card,
    borderRadius: RADIUS.lg,
    padding: SPACING.md,
    marginBottom: SPACING.md,
    borderWidth: 2,
    borderColor: COLORS.border,
  },
  methodCardSelected: {
    borderColor: COLORS.accent,
    backgroundColor: COLORS.accent + '10',
  },
  methodIcon: {
    width: 50,
    height: 50,
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.bg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: SPACING.md,
  },
  methodInfo: {
    flex: 1,
  },
  methodName: {
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    color: COLORS.text,
  },
  methodDesc: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
    marginTop: 2,
  },
  input: {
    backgroundColor: COLORS.bg,
    borderRadius: RADIUS.sm,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: SPACING.md,
    color: COLORS.text,
    fontSize: FONT_SIZE.md,
    marginBottom: SPACING.md,
  },
  cardRow: {
    flexDirection: 'row',
    gap: SPACING.md,
  },
  cardInput: {
    flex: 1,
    marginBottom: 0,
  },
  helperText: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
    marginTop: -SPACING.md,
    marginBottom: SPACING.md,
  },
  bankCard: {
    padding: SPACING.lg,
    backgroundColor: COLORS.card,
  },
  bankInfo: {
    marginBottom: SPACING.lg,
  },
  bankLabel: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
    marginBottom: 4,
  },
  bankValue: {
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    color: COLORS.accent,
    fontFamily: 'monospace',
  },
  bankNote: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
    marginTop: SPACING.md,
    fontStyle: 'italic',
  },
  cashCard: {
    padding: SPACING.xl,
    alignItems: 'center',
    backgroundColor: COLORS.success + '10',
  },
  cashTitle: {
    fontSize: FONT_SIZE.lg,
    fontWeight: '700',
    color: COLORS.text,
    marginTop: SPACING.md,
  },
  cashDesc: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
    marginTop: SPACING.sm,
    textAlign: 'center',
  },
  footer: {
    padding: SPACING.lg,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
  },
  payButton: {
    backgroundColor: COLORS.accent,
    borderRadius: RADIUS.lg,
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
  },
  payButtonDisabled: {
    opacity: 0.6,
  },
  payButtonText: {
    color: COLORS.text,
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    marginLeft: SPACING.sm,
  },
  errorText: {
    color: COLORS.danger,
    marginBottom: SPACING.sm,
    fontSize: FONT_SIZE.sm,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: SPACING.lg,
  },
  confirmModal: {
    backgroundColor: COLORS.card,
    borderRadius: RADIUS.lg,
    padding: SPACING.lg,
    width: '100%',
    maxWidth: 400,
  },
  confirmTitle: {
    fontSize: FONT_SIZE.lg,
    fontWeight: '700',
    color: COLORS.text,
    marginBottom: SPACING.lg,
    textAlign: 'center',
  },
  confirmInfo: {
    marginBottom: SPACING.md,
    paddingBottom: SPACING.md,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  confirmLabel: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
  },
  confirmValue: {
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    color: COLORS.text,
    marginTop: 4,
  },
  confirmValueLarge: {
    fontSize: FONT_SIZE.xxl,
    fontWeight: '700',
    color: COLORS.accent,
    marginTop: 4,
  },
  confirmActions: {
    flexDirection: 'row',
    gap: SPACING.md,
    marginTop: SPACING.lg,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.bg,
    alignItems: 'center',
  },
  confirmButtonPrimary: {
    backgroundColor: COLORS.accent,
  },
  confirmButtonText: {
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    color: COLORS.text,
  },
  confirmButtonTextPrimary: {
    color: COLORS.text,
  },
});
